/******************************************************************************
 * first-okawari-v2.js
 *
 * Front-end orchestration for:
 *  1. Checking if an email+device combo exists (and tagging existing customers), and
 *  2. Onboarding brand-new users (creating account + tagging).
 *
 * Entry points:
 *  - form “submit” ⇒ checkEmail flow
 *  - #register-btn “click” ⇒ addEmail flow
 ******************************************************************************/

function getConfigValue(id, fallback = null) {
  const el = document.getElementById(id);
  return el ? el.dataset.value : fallback;
}

const API_CHECK_EMAIL_URL = getConfigValue("ecoco-check-email-baseUrl");
const API_CE_ENDPOINT = getConfigValue("ecoco-check-email-endpoint");
const API_ADD_EMAIL_URL = getConfigValue("ecoco-add-email-baseUrl");
const API_AE_ENDPOINT = getConfigValue("ecoco-add-email-endpoint");

if (!API_CHECK_EMAIL_URL || !API_CE_ENDPOINT) {
  console.warn(
    "Missing Check Email API config. Check if you are logged in and AWS Setup is complete."
  );
}
if (!API_ADD_EMAIL_URL || !API_AE_ENDPOINT) {
  console.warn(
    "Missing Add Email API config. Check if you are logged in and AWS Setup is complete."
  );
}

/**
 * addEmail({ email, password, serialNumber, phone })
 *
 * For brand-new customers:
 *   • Posts to our “add-email” Lambda
 *   • Creates a Shopify customer account
 *   • Tags that customer with the device serial under “ecoco-sn:…”
 *   • Phone is optional.
 *   • Returns true if account creation succeeded
 */
async function addEmail({ email, password, serialNumber, phone }) {
  try {
    // Normalize phone if provided
    let normalizedPhone = null;
    if (phone && phone.trim()) {
      if (window.libphonenumber && window.libphonenumber.parsePhoneNumber) {
        try {
          const raw = phone.trim();
          let phoneNumber;
          if (raw.startsWith('+')) {
            phoneNumber = window.libphonenumber.parsePhoneNumber(raw);
          } else {
            phoneNumber = window.libphonenumber.parsePhoneNumber(raw, 'JP');
          }
          if (phoneNumber && phoneNumber.isValid()) {
            normalizedPhone = phoneNumber.format('E.164');
          }
        } catch (err) {
          console.warn('Phone normalization failed:', err);
        }
      }
    }

    const payload = { email, password, serialNumber };
    if (normalizedPhone) payload.phone = normalizedPhone;
    const response = await fetch(`${API_ADD_EMAIL_URL}/${API_AE_ENDPOINT}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    let data = {};
    try {
      data = await response.json();
    } catch (e) {
      // If response is not JSON, fallback to empty object
      data = {};
    }
    if (!response.ok) {
      return {
        ok: false,
        error: data.error || `HTTP ${response.status}`,
        details: data.details || data.errors || null,
      };
    }
    const created = data.customer && data.customer.id;
    if (created) return { ok: true };

    console.warn("Unexpected response shape or error:", data);
    return { ok: false, error: data.error || "Unexpected response", details: data.details || data.errors || null };
  } catch (err) {
    console.error("Error in addEmail:", err);
    return { ok: false, error: err.message || "Network error", details: null };
  }
}
/**
 * checkEmail({ email, serialNumber })
 *
 * For existing customers:
 *   • Posts to our “check-email” Lambda
 *   • Lambda enriches the customer’s tags with the new device serial
 *   • Returns an object { exists: boolean, tags: Array<String> }
 */
async function checkEmail({ email, serialNumber }) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000); // 8s timeout

  try {
    const response = await fetch(`${API_CHECK_EMAIL_URL}/${API_CE_ENDPOINT}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, serialNumber }),
      signal: controller.signal,
    });
    let data = {};
    try {
      data = await response.json();
    } catch (e) {
      data = {};
    }
    if (!response.ok) {
      // If the API returns exists: true, treat as success
      if (data.exists) {
        return data;
      }
      throw new Error(`HTTP ${response.status}`);
    }
    return data;
  } catch (err) {
    console.warn("checkEmail failed once, retrying:", err.message);
    clearTimeout(timeout);
    // Retry once after short backoff
    await new Promise(r => setTimeout(r, 600));
    try {
      const retryResp = await fetch(`${API_CHECK_EMAIL_URL}/${API_CE_ENDPOINT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, serialNumber }),
      });
      const retryData = await retryResp.json().catch(() => ({}));
      if (retryResp.ok || retryData.exists) return retryData;
      throw new Error(`Retry failed: ${retryResp.status}`);
    } catch (retryErr) {
      console.error("checkEmail hard fail:", retryErr);
      return null; // frontend shows snackbar
    }
  }
}
/**
 * showModal(id)
 * hideModal(id)
 *
 * Utility functions to toggle modal visibility
 * using an “is-visible” CSS class.
 */
function showModal(id) {
  const modal = document.getElementById(id);
  if (modal) {
    modal.classList.remove("hidden");
    modal.classList.add("is-visible");
  }
}

function hideModal(id) {
  const modal = document.getElementById(id);
  if (modal) {
    modal.classList.remove("is-visible");
    modal.classList.add("hidden");
  }
}
/**
 * DOMContentLoaded
 *
 * Wire up:
 *  - Form validation + “Check & Tag” on submit
 *  - Password-strength UI
 *  - “Register” flow for new users
 *  - Escape-key closing of modals
 */
document.addEventListener("DOMContentLoaded", () => {
  // Element references
  const form = document.getElementById("check-email-form");
  const submitInput = form.querySelector("input[type=submit]");
  const emailField = form.querySelector("input[name='entry.1970027461']");
  const serialField = document.getElementById("register-serial");
  const codeField = form.querySelector("input[name='entry.129508646']");
  const modalRegister = document.getElementById("modal-register");
  const pwdInput = document.getElementById("register-password");
  const phoneField = modalRegister.querySelector("#register-phone");
  const toggleBtn = document.querySelector(".toggle-password");
  const meterFill = document.querySelector(".password-meter-fill");
  const strengthLabel = document.querySelector(".password-strength-label");
  const passwordError = modalRegister.querySelector("#passwordError");
  const phoneError = modalRegister.querySelector("#phoneError");
  const registerBtn = modalRegister.querySelector("#register-btn");
  let isNewCustomer = false;
  function showConfirmationModal() {
    showModal(
      isNewCustomer ? "modal-confirmation-new" : "modal-confirmation"
    );
  }
  // Password-strength UI & validation
  function updateRegisterBtn() {
    registerBtn.disabled = !pwdInput.validity.valid;
  }

  function updatePasswordError() {
    if (pwdInput.validity.valueMissing) {
      passwordError.textContent = "パスワードは必須です。";
    } else if (pwdInput.validity.tooShort) {
      passwordError.textContent = `パスワードは最低 ${pwdInput.minLength} 文字以上で入力してください。`;
    } else {
      passwordError.textContent = "";
    }
  }

  toggleBtn.addEventListener("click", () => {
    const show = pwdInput.type === "password";
    pwdInput.type = show ? "text" : "password";
    toggleBtn.innerHTML = show
      ? '<i class="fa-solid fa-eye-slash"></i>'
      : '<i class="fa-solid fa-eye"></i>';
  });

  function scorePassword(pw) {
    let score = 0;
    if (pw.length >= 6) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/\d/.test(pw) && /[\W_]/.test(pw)) score++;
    return score; // 0..3
  }

  pwdInput.addEventListener("input", () => {
    updateRegisterBtn();
    const val = pwdInput.value;
    const score = scorePassword(val);
    let pct, bg, text;

    if (!val) {
      pct = 0;
      bg = "#ececec";
      text = "";
    } else if (score <= 1) {
      pct = 33;
      bg = "#e53935";
      text = "弱い";
    } else if (score === 2) {
      pct = 66;
      bg = "#fbc02d";
      text = "普通";
    } else {
      pct = 100;
      bg = "#43a047";
      text = "強い";
    }

    strengthLabel.textContent = text ? `パスワード強度: ${text}` : "";
    meterFill.style.display = "block";
    meterFill.style.width = pct + "%";
    meterFill.style.backgroundColor = bg;

    updatePasswordError();
    updateRegisterBtn();
  });

  pwdInput.addEventListener("blur", () => {
    updatePasswordError();
    updateRegisterBtn();
  });
  if (phoneField && phoneError) {
    phoneField.addEventListener("input", () => {
      phoneError.textContent = "";
    });
  }

  updatePasswordError();
  updateRegisterBtn();

  // Force a full reload when returning via back/forward cache so a fresh submission can always occur.
  window.addEventListener("pageshow", (evt) => {
    // bfcache restore -> evt.persisted true OR navigation entry type back_forward
    let isBackForward = !!evt.persisted;
    try {
      const navEntries = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (navEntries && navEntries[0] && navEntries[0].type === 'back_forward') isBackForward = true;
      if (window.performance && window.performance.navigation && window.performance.navigation.type === 2) isBackForward = true; // legacy
    } catch(_) {}
    if (isBackForward) {
      // Hard reload ensures closure state (submittedToGoogle) resets and Google form can post again.
      window.location.reload();
    }
  });
  // Form validation configuration
  let fields = [
    {
      input: emailField,
      errorEl: document.getElementById("emailError"),
      messages: {
        valueMissing: "メールアドレスを入力してください。",
        typeMismatch: "有効なメール形式で入力してください。",
      },
    },
    {
      input: serialField,
      errorEl: document.getElementById("serialError"),
      messages: {
        valueMissing: "シリアルナンバーは必須です。",
        patternMismatch: "13桁の数字を入力してください。",
      },
    },
    {
      input: codeField,
      errorEl: document.getElementById("codeError"),
      messages: {
        valueMissing: "コードは必須です。",
        patternMismatch: "11文字の英数字を入力してください。",
      },
    },
  ];

  // Tighten email validation: require "something@something.tld"
  if (emailField) {
    // ensure native constraints
    if (emailField.getAttribute("type")?.toLowerCase() !== "email") {
      emailField.setAttribute("type", "email");
    }
    emailField.required = true;

    // only add if not already provided in HTML
    if (!emailField.getAttribute("pattern")) {
      // Accept multi-label domains and allow double dots between labels; disallow trailing dot
      emailField.setAttribute(
        "pattern",
        "^[^\\s@]+@[^@\\s.]+(?:\\.[^@\\s]*)*[^.@\\s]$"
      );
    }

    // make sure we have a nice message for patternMismatch
    const ef = fields.find(f => f.input === emailField);
    if (ef && !ef.messages.patternMismatch) {
      ef.messages.patternMismatch = "有効なメール形式で入力してください。（例: name@example.com/name@example.co.jp）";
    }
  }


  // Guard: drop any fields whose input element isn't present
  fields = fields.filter(f => !!f.input);

  // Ensure native constraints exist (so validity actually fires)
  fields.forEach(({ input }) => {
    if (input === emailField) {
      if (input.getAttribute("type")?.toLowerCase() !== "email") {
        input.setAttribute("type", "email"); // enables typeMismatch
      }
      input.required = true;
    } else if (input === serialField) {
      input.required = true;
      if (!input.getAttribute("pattern")) {
        input.setAttribute("pattern", "^\\d{13}$"); // 13 digits
      }
    } else if (input === codeField) {
      input.required = true;
      if (!input.getAttribute("pattern")) {
        input.setAttribute("pattern", "^[A-Za-z0-9]{11}$"); // 11 alphanumeric characters
      }
    }
  });

  // Track interaction for better UX
  // - touched: user interacted at least once
  // - lastEventType: 'input' or 'blur' to decide when to show required-on-empty
  const touched = new WeakMap();
  const lastEventType = new WeakMap();
  fields.forEach(({ input }) => {
    touched.set(input, false);
    lastEventType.set(input, null);
  });

  // Build a message from native validity
  function buildMessage(input, messages) {
    if (input.validity.valueMissing) return messages.valueMissing;
    if (input.validity.typeMismatch) return messages.typeMismatch || "";
    if (input.validity.patternMismatch) return messages.patternMismatch || "";
    return "";
  }

  // Validate one field. Show errors:
  //  - while typing, but only for non-empty values (pattern/type mismatches)
  //  - on blur, including required-on-empty
  function validateField({ input, errorEl, messages }) {
    const msg = buildMessage(input, messages);
    const hasValue = (input.value || '').length > 0;
    const lastEvt = lastEventType.get(input);
    // Show when user has started typing and there is content, or after blur
    const shouldShow = (hasValue && touched.get(input) === true) || lastEvt === 'blur';
    // Suppress the "required" message while typing an empty field
    const isOnlyRequired = !hasValue && input.validity.valueMissing && lastEvt !== 'blur';
    errorEl.textContent = shouldShow && !isOnlyRequired ? msg : "";
    return msg === "";
  }

  // Update button + render errors per touched state
  function updateFormState() {
    // Validate ALL fields (no short-circuit), then compute overall validity
    let allValid = true;
    fields.forEach((f) => {
      const valid = f.input.checkValidity();
      validateField(f);
      if (!valid) allValid = false;
    });
    submitInput.disabled = !allValid;
  }

  // Wire events: first input/blur marks touched and revalidate
  fields.forEach((f) => {
    f.input.addEventListener("input", () => {
      if (!touched.get(f.input)) touched.set(f.input, true);
      lastEventType.set(f.input, 'input');
      updateFormState();
    });
    f.input.addEventListener("blur", () => {
      if (!touched.get(f.input)) touched.set(f.input, true);
      lastEventType.set(f.input, 'blur');
      updateFormState();
    });
  });

  // Initial: errors hidden, button disabled until valid
  submitInput.disabled = true;


  function showSnackbar(message) {
    let snackbar = document.getElementById("ecoco-snackbar");
    if (!snackbar) {
      snackbar = document.createElement("div");
      snackbar.id = "ecoco-snackbar";
      snackbar.style.position = "fixed";
      snackbar.style.bottom = "2rem";
      snackbar.style.left = "50%";
      snackbar.style.transform = "translateX(-50%)";
      snackbar.style.background = "#e53935";
      snackbar.style.color = "#fff";
      snackbar.style.padding = "1rem 2rem";
      snackbar.style.borderRadius = "8px";
      snackbar.style.zIndex = "9999";
      snackbar.style.fontSize = "1.2rem";
      document.body.appendChild(snackbar);
    }
    snackbar.textContent = message;
    snackbar.style.display = "block";
    setTimeout(() => {
      snackbar.style.display = "none";
    }, 4000);
  }

  let submittedToGoogle = false;
  function submitToGoogleSilently() {
    if (submittedToGoogle) return true;
    try {
      form.submit();
      submittedToGoogle = true;
      return true;
    } catch (e) {
      console.warn("Google form submit failed:", e);
      return false;
    }
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    updateFormState();
    // If a previous Google submission already happened, leave this page to reset state
    if (submittedToGoogle) {
      window.location.href = isNewCustomer ? "/pages/okawari-thanks-new" : "/pages/okawari-thanks";
      return;
    }
    if (submitInput.disabled) return;

    submitInput.disabled = true;
    submitInput.value = "検証中…";
    
    // Immediately submit to Google Forms (hidden iframe) so the row is recorded regardless of user choice in modal.
    submitToGoogleSilently();

    const email = emailField.value.trim();
    const serialNumber = serialField.value.trim();
    try {
      const result = await checkEmail({ email, serialNumber });
      if (!result) {
        showSnackbar("サーバーエラーが発生しました。後でもう一度お試しください。");
        return;
      }
      const { exists, tags = [] } = result;
      if (exists) {
        showConfirmationModal();
      } else {
        isNewCustomer = true;
        modalRegister.querySelector("#register-email").value = email;
        showModal("modal-register");
      }
    } catch (err) {
      console.error("Email check failed:", err);
      showSnackbar("サーバーエラーが発生しました。後でもう一度お試しください。");
    } finally {
      submitInput.disabled = false;
      submitInput.value = "送信";
    }
  });
  
  registerBtn.addEventListener("click", async () => {
    if (registerBtn.disabled) return;
    
    const email = emailField.value.trim();
    const serialNumber = serialField.value.trim();
    const password = pwdInput.value;
    const phone = phoneField ? phoneField.value.trim() : "";
    
    registerBtn.disabled = true;
    registerBtn.querySelector("span").textContent = "登録中…";
    
    try {
      const result = await addEmail({ email, password, serialNumber, phone });
      if (result.ok) {
        hideModal("modal-register");
        showConfirmationModal();
      } else {
        if (phoneError) phoneError.textContent = "";
        passwordError.textContent = "";

        const details = result.details || {};
        if (details.phone && details.phone.length) {
          const msg = details.phone[0];
          phoneError.textContent =
            msg === "has already been taken"
              ? "この電話番号は既に登録されています。"
              : `電話番号: ${msg}`;
        } else if (details.email && details.email.length) {
          const msg = details.email[0];
          passwordError.textContent =
            msg === "has already been taken"
              ? "このメールアドレスは既に登録されています。"
              : `メール: ${msg}`;
        } else if (details.password && details.password.length) {
          passwordError.textContent = `パスワード: ${details.password[0]}`;
        } else {
          passwordError.textContent =
            "登録に失敗しました。もう一度お試しください。";
        }
        showModal("modal-register");
      }
    } finally {
      registerBtn.disabled = !pwdInput.validity.valid;
      registerBtn.querySelector("span").textContent = "登録する";
    }
  });

  // Modal actions: user decides next step
  const btnCancelConfirm = document.getElementById("okawari-confirm-cancel");
  if (btnCancelConfirm) {
    btnCancelConfirm.addEventListener("click", () => {
      // Fallback attempt only if earlier submission failed
      submitToGoogleSilently();
      window.location.href = isNewCustomer ? "/pages/okawari-thanks-new" : "/pages/okawari-thanks";
    });
  }
  const btnCancelConfirmNew = document.getElementById("okawari-confirm-cancel-new");
  if (btnCancelConfirmNew) {
    btnCancelConfirmNew.addEventListener("click", () => {
      // Fallback attempt only if earlier submission failed
      submitToGoogleSilently();
      window.location.href = isNewCustomer ? "/pages/okawari-thanks-new" : "/pages/okawari-thanks";
    });
  }
  const btnGoLogin = document.getElementById("okawari-confirm-login");
  if (btnGoLogin) {
    btnGoLogin.addEventListener("click", () => {
      submitToGoogleSilently();
      window.location.href = "/account";
    });
  }
  const btnGoLoginNew = document.getElementById("okawari-confirm-login-new");
  if (btnGoLoginNew) {
    btnGoLoginNew.addEventListener("click", () => {
      submitToGoogleSilently();
      window.location.href = "/account";
    });
  }
  const btnCancelRegister = document.getElementById("okawari-register-cancel");
  if (btnCancelRegister) {
    btnCancelRegister.addEventListener("click", () => {
      submitToGoogleSilently();
      window.location.href = isNewCustomer ? "/pages/okawari-thanks-new" : "/pages/okawari-thanks";
    });
  }
  
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      // Treat Escape like Cancel: ensure Google submit then redirect to thanks
      submitToGoogleSilently();
      window.location.href = isNewCustomer ? "/pages/okawari-thanks-new" : "/pages/okawari-thanks";
    }
  });
  // 4) Close button (X) should also act like Cancel
  document.querySelectorAll('.modal .modal-close').forEach((btn) => {
    btn.addEventListener('click', () => {
      submitToGoogleSilently();
      window.location.href = isNewCustomer ? "/pages/okawari-thanks-new" : "/pages/okawari-thanks";
    });
  });
});
